/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import dagre from 'dagre'
import { Node, Edge } from 'reactflow'
import { CargoOrganogramaResponse, iTrilhaCarreiraCompleta } from '../../../../../interfaces'

export function montarGrafoPadraoDeCargos(
  cargos: CargoOrganogramaResponse[]
): { nos: Node[], ligacoes: Edge[] } {
  const nos: Node[] = []
  const ligacoes: Edge[] = []
  let deslocamentoYAcumulado = 0

  function obterProfundidadeMaxima(cargo: CargoOrganogramaResponse, profundidade = 1): number {
    if (!cargo.subordinados || cargo.subordinados.length === 0) return profundidade
    return Math.max(...cargo.subordinados.map(subordinado => obterProfundidadeMaxima(subordinado, profundidade + 1)))
  }

  cargos.forEach(cargoRaiz => {
    const profundidade = obterProfundidadeMaxima(cargoRaiz)
    const deslocamentoY = deslocamentoYAcumulado
    const nivelPorCargoId: Record<string, number> = {}

    const grafo = new dagre.graphlib.Graph()
    grafo.setGraph({ rankdir: 'TB', nodesep: 50, ranksep: 130 })
    grafo.setDefaultEdgeLabel(() => ({}))

    function percorrer(cargo: CargoOrganogramaResponse, cargoSuperiorId?: string, nivel = 0) {
      const cargoId = cargo.id
      grafo.setNode(cargoId, { width: 350, height: 100 })
      if (cargoSuperiorId) grafo.setEdge(cargoSuperiorId, cargoId)
      nivelPorCargoId[cargoId] = nivel
      cargo.subordinados?.forEach(subordinado => percorrer(subordinado, cargoId, nivel + 1))
    }

    percorrer(cargoRaiz)

    dagre.layout(grafo)

    const coordenadasX = grafo.nodes().map(cargoId => grafo.node(cargoId).x)
    const menorX = Math.min(...coordenadasX)
    const maiorX = Math.max(...coordenadasX)
    const larguraOcupada = maiorX - menorX
    const larguraAlvo = 1500
    const deslocamentoParaCentralizar = (larguraAlvo - larguraOcupada) / 2 - menorX

    grafo.nodes().forEach((cargoId: any) => {
      const posicaoNoGrafo = grafo.node(cargoId)
      const cargo = encontrarCargoPorId([cargoRaiz], cargoId)
      if (!cargo) return

      nos.push({
        id: cargoId,
        type: 'custom',
        deletable: false,
        data: {
          id: cargoId,
          label: cargo.nomeCargo,
          nivel: nivelPorCargoId[cargoId],
          niveisPontuados: cargo.niveisPontuados
        },
        position: {
          x: posicaoNoGrafo.x + deslocamentoParaCentralizar,
          y: posicaoNoGrafo.y + deslocamentoY
        },
        width: 350,
        height: 100
      })
    })

    grafo.edges().forEach(ligacao => {
      ligacoes.push({
        id: `e${ligacao.v}-${ligacao.w}`,
        source: ligacao.v,
        target: ligacao.w
      })
    })

    deslocamentoYAcumulado += profundidade * 120
  })

  return { nos, ligacoes }
}

export function montarGrafoDoLayoutSalvo(
  layout: iTrilhaCarreiraCompleta,
  cargos: CargoOrganogramaResponse[],
  aoExcluirNoRemovido: (cargoId: string) => void
): { nos: Node[], ligacoes: Edge[], possuiCargosNovos: boolean } {
  const cargoPorId = mapearCargosPorId(cargos)

  const nos: Node[] = layout.nodes.map(noSalvo => {
    const cargo = cargoPorId.get(noSalvo.data.cargoId)
    const removido = !cargo

    // Só o nó de um cargo que sumiu do cadastro pode ser tirado do fluxo.
    // Os demais só saem restaurando o layout padrão.
    return {
      id: noSalvo.id,
      type: noSalvo.type || 'custom',
      deletable: removido,
      data: {
        id: noSalvo.data.cargoId,
        label: cargo ? cargo.nomeCargo : 'Cargo removido do cadastro',
        niveisPontuados: cargo ? cargo.niveisPontuados : [],
        removido: removido,
        aoExcluir: removido ? aoExcluirNoRemovido : undefined
      },
      position: noSalvo.position,
      width: noSalvo.width,
      height: noSalvo.height
    }
  })

  const cargoIdsNoLayout = new Set(layout.nodes.map(noSalvo => noSalvo.data.cargoId))
  const cargosNovos = Array.from(cargoPorId.values()).filter(cargo => !cargoIdsNoLayout.has(cargo.id))

  if (cargosNovos.length === 0) return { nos, ligacoes: ligacoesEntreNos(cargos, nos), possuiCargosNovos: false }

  // Cargo cadastrado depois do layout salvo entra numa linha abaixo do desenho atual.
  const LARGURA_NO = 350
  const ALTURA_NO = 100
  const ESPACO = 80
  const temNosSalvos = layout.nodes.length > 0
  const menorX = temNosSalvos ? Math.min(...layout.nodes.map(noSalvo => noSalvo.position.x)) : 0
  const maiorY = temNosSalvos
    ? Math.max(...layout.nodes.map(noSalvo => noSalvo.position.y + (noSalvo.height || ALTURA_NO))) + ESPACO
    : 0

  cargosNovos.forEach((cargo, indice) => {
    nos.push({
      id: cargo.id,
      type: 'custom',
      deletable: false,
      data: {
        id: cargo.id,
        label: cargo.nomeCargo,
        niveisPontuados: cargo.niveisPontuados
      },
      position: {
        x: menorX + indice * (LARGURA_NO + ESPACO),
        y: maiorY
      },
      width: LARGURA_NO,
      height: ALTURA_NO
    })
  })

  return { nos, ligacoes: ligacoesEntreNos(cargos, nos), possuiCargosNovos: true }
}

export function montarLigacoesDoCadastro(cargos: CargoOrganogramaResponse[]): Edge[] {
  const ligacoes = new Map<string, Edge>()
  const visitados = new Set<string>()

  function percorrer(lista: CargoOrganogramaResponse[]): void {
    for (const cargo of lista) {
      if (visitados.has(cargo.id)) continue
      visitados.add(cargo.id)
      for (const subordinado of cargo.subordinados ?? []) {
        const id = `e${cargo.id}-${subordinado.id}`
        if (!ligacoes.has(id)) ligacoes.set(id, { id, source: cargo.id, target: subordinado.id })
      }
      percorrer(cargo.subordinados ?? [])
    }
  }

  percorrer(cargos)
  return Array.from(ligacoes.values())
}

function ligacoesEntreNos(cargos: CargoOrganogramaResponse[], nos: Node[]): Edge[] {
  const idsDosNos = new Set(nos.map(no => no.id))
  return montarLigacoesDoCadastro(cargos).filter(ligacao => idsDosNos.has(ligacao.source) && idsDosNos.has(ligacao.target))
}

// Distribui os cargos em níveis a partir da hierarquia, separando trilhas
// desconexas em blocos lado a lado. Substitui o posicionamento do dagre.
export function aplicarLayoutHierarquico(
  cargos: CargoOrganogramaResponse[],
  nos: any[]
): any[] {
  const LARGURA_NO = 420
  const ESPACO_HORIZONTAL = 80
  const ESPACO_ENTRE_BLOCOS = 220
  const TOLERANCIA_MEDIA = 30
  const ESCALA_VERTICAL = 1.5
  const DISTANCIA_MINIMA_ENTRE_LINHAS = 180

  const noPorCargoId = new Map<string, any>()
  nos.forEach((no) => {
    noPorCargoId.set(no.id, no)
  })

  const cargoPorId = new Map<string, CargoOrganogramaResponse>()
  const subordinadosPorCargoId = new Map<string, string[]>()
  const superioresPorCargoId = new Map<string, string[]>()

  function garantirRegistroNosMapas(cargoId: string): void {
    if (!subordinadosPorCargoId.has(cargoId)) subordinadosPorCargoId.set(cargoId, [])
    if (!superioresPorCargoId.has(cargoId)) superioresPorCargoId.set(cargoId, [])
  }

  function percorrerHierarquia(cargo: CargoOrganogramaResponse): void {
    cargoPorId.set(cargo.id, cargo)
    garantirRegistroNosMapas(cargo.id)

    const subordinados = cargo.subordinados ?? []

    for (let i = 0; i < subordinados.length; i++) {
      const subordinado = subordinados[i]

      cargoPorId.set(subordinado.id, subordinado)
      garantirRegistroNosMapas(subordinado.id)

      const subordinadosAtuais = subordinadosPorCargoId.get(cargo.id) ?? []
      if (!subordinadosAtuais.includes(subordinado.id)) {
        subordinadosAtuais.push(subordinado.id)
        subordinadosPorCargoId.set(cargo.id, subordinadosAtuais)
      }

      const superioresAtuais = superioresPorCargoId.get(subordinado.id) ?? []
      if (!superioresAtuais.includes(cargo.id)) {
        superioresAtuais.push(cargo.id)
        superioresPorCargoId.set(subordinado.id, superioresAtuais)
      }

      percorrerHierarquia(subordinado)
    }
  }

  for (let i = 0; i < cargos.length; i++) {
    percorrerHierarquia(cargos[i])
  }

  const todosOsCargoIds = Array.from(cargoPorId.keys())

  // monta a adjacência não direcionada para achar os blocos desconexos
  const vizinhosPorCargoId = new Map<string, string[]>()
  for (let i = 0; i < todosOsCargoIds.length; i++) {
    vizinhosPorCargoId.set(todosOsCargoIds[i], [])
  }

  subordinadosPorCargoId.forEach((subordinados, cargoSuperiorId) => {
    for (let i = 0; i < subordinados.length; i++) {
      const subordinadoId = subordinados[i]
      const vizinhosDoSuperior = vizinhosPorCargoId.get(cargoSuperiorId) ?? []
      const vizinhosDoSubordinado = vizinhosPorCargoId.get(subordinadoId) ?? []

      if (!vizinhosDoSuperior.includes(subordinadoId)) vizinhosDoSuperior.push(subordinadoId)
      if (!vizinhosDoSubordinado.includes(cargoSuperiorId)) vizinhosDoSubordinado.push(cargoSuperiorId)

      vizinhosPorCargoId.set(cargoSuperiorId, vizinhosDoSuperior)
      vizinhosPorCargoId.set(subordinadoId, vizinhosDoSubordinado)
    }
  })

  const visitados = new Set<string>()
  const blocos: string[][] = []

  for (let i = 0; i < todosOsCargoIds.length; i++) {
    const cargoIdInicial = todosOsCargoIds[i]
    if (visitados.has(cargoIdInicial)) continue

    const bloco: string[] = []
    const pilha = [cargoIdInicial]
    visitados.add(cargoIdInicial)

    while (pilha.length) {
      const cargoIdAtual = pilha.pop() as string
      bloco.push(cargoIdAtual)

      const vizinhos = vizinhosPorCargoId.get(cargoIdAtual) ?? []
      for (let j = 0; j < vizinhos.length; j++) {
        const vizinho = vizinhos[j]
        if (!visitados.has(vizinho)) {
          visitados.add(vizinho)
          pilha.push(vizinho)
        }
      }
    }

    blocos.push(bloco)
  }

  blocos.sort((a, b) => b.length - a.length)

  const nosPosicionados = new Map<string, any>()
  let deslocamentoXDoBloco = 0

  for (let indiceBloco = 0; indiceBloco < blocos.length; indiceBloco++) {
    const cargoIdsDoBloco = blocos[indiceBloco]
    const cargosDoBloco = new Set(cargoIdsDoBloco)

    // grau de entrada considerando apenas as ligações internas do bloco
    const grauDeEntrada = new Map<string, number>()
    for (let i = 0; i < cargoIdsDoBloco.length; i++) {
      grauDeEntrada.set(cargoIdsDoBloco[i], 0)
    }

    for (let i = 0; i < cargoIdsDoBloco.length; i++) {
      const cargoSuperiorId = cargoIdsDoBloco[i]
      const subordinados = subordinadosPorCargoId.get(cargoSuperiorId) ?? []

      for (let j = 0; j < subordinados.length; j++) {
        const subordinadoId = subordinados[j]
        if (!cargosDoBloco.has(subordinadoId)) continue
        grauDeEntrada.set(subordinadoId, (grauDeEntrada.get(subordinadoId) ?? 0) + 1)
      }
    }

    // Kahn: ordem topológica
    const fila: string[] = []
    for (let i = 0; i < cargoIdsDoBloco.length; i++) {
      const cargoId = cargoIdsDoBloco[i]
      if ((grauDeEntrada.get(cargoId) ?? 0) === 0) {
        fila.push(cargoId)
      }
    }

    const ordemTopologica: string[] = []
    while (fila.length) {
      const cargoIdAtual = fila.shift() as string
      ordemTopologica.push(cargoIdAtual)

      const subordinados = subordinadosPorCargoId.get(cargoIdAtual) ?? []
      for (let i = 0; i < subordinados.length; i++) {
        const subordinadoId = subordinados[i]
        if (!cargosDoBloco.has(subordinadoId)) continue

        const grauRestante = (grauDeEntrada.get(subordinadoId) ?? 0) - 1
        grauDeEntrada.set(subordinadoId, grauRestante)

        if (grauRestante === 0) {
          fila.push(subordinadoId)
        }
      }
    }

    // fallback para hierarquias com ciclo, que o Kahn não consegue ordenar
    for (let i = 0; i < cargoIdsDoBloco.length; i++) {
      const cargoId = cargoIdsDoBloco[i]
      if (!ordemTopologica.includes(cargoId)) {
        ordemTopologica.push(cargoId)
      }
    }

    // altura proporcional à média de pontuação; médias a até TOLERANCIA_MEDIA da primeira da faixa ficam na mesma altura
    const mediaPorCargoId = new Map<string, number>()
    for (let i = 0; i < cargoIdsDoBloco.length; i++) {
      const cargoId = cargoIdsDoBloco[i]
      mediaPorCargoId.set(cargoId, calcularMediaDePontuacao(cargoPorId.get(cargoId)))
    }

    const cargosPorMedia = ordemTopologica.slice().sort((a, b) => (mediaPorCargoId.get(b) ?? 0) - (mediaPorCargoId.get(a) ?? 0))
    const maiorMedia = mediaPorCargoId.get(cargosPorMedia[0]) ?? 0

    const yPorCargoId = new Map<string, number>()
    let mediaDaFaixa = Number.MAX_SAFE_INTEGER

    for (let i = 0; i < cargosPorMedia.length; i++) {
      const cargoId = cargosPorMedia[i]
      const media = mediaPorCargoId.get(cargoId) ?? 0
      if (mediaDaFaixa - media > TOLERANCIA_MEDIA) mediaDaFaixa = media
      yPorCargoId.set(cargoId, (maiorMedia - mediaDaFaixa) * ESCALA_VERTICAL)
    }

    // a média só ordena quem está abaixo do mesmo superior: o cargo nunca sobe acima de nenhum superior
    for (let i = 0; i < ordemTopologica.length; i++) {
      const cargoId = ordemTopologica[i]
      const superiores = (superioresPorCargoId.get(cargoId) ?? []).filter((superiorId) => cargosDoBloco.has(superiorId) && yPorCargoId.has(superiorId))
      let y = yPorCargoId.get(cargoId) ?? 0

      for (let j = 0; j < superiores.length; j++) {
        y = Math.max(y, (yPorCargoId.get(superiores[j]) ?? 0) + DISTANCIA_MINIMA_ENTRE_LINHAS)
      }

      yPorCargoId.set(cargoId, y)
    }

    // layout de árvore: cada ramo ocupa suas próprias colunas, o superior fica centralizado sobre os subordinados.
    // Com mais de um superior, o cargo fica no ramo do superior mais baixo.
    const superiorPrincipalPorCargoId = new Map<string, string>()
    for (let i = 0; i < ordemTopologica.length; i++) {
      const cargoId = ordemTopologica[i]
      const superiores = (superioresPorCargoId.get(cargoId) ?? []).filter((superiorId) => cargosDoBloco.has(superiorId) && (yPorCargoId.get(superiorId) ?? 0) < (yPorCargoId.get(cargoId) ?? 0))
      if (!superiores.length) continue

      superiores.sort((a, b) => (yPorCargoId.get(b) ?? 0) - (yPorCargoId.get(a) ?? 0))
      superiorPrincipalPorCargoId.set(cargoId, superiores[0])
    }

    const filhosPorCargoId = new Map<string, string[]>()
    const raizes: string[] = []
    for (let i = 0; i < cargosPorMedia.length; i++) {
      const cargoId = cargosPorMedia[i]
      const superiorId = superiorPrincipalPorCargoId.get(cargoId)

      if (!superiorId) {
        raizes.push(cargoId)
        continue
      }

      if (!filhosPorCargoId.has(superiorId)) filhosPorCargoId.set(superiorId, [])
      ;(filhosPorCargoId.get(superiorId) as string[]).push(cargoId)
    }

    const colunaPorCargoId = new Map<string, number>()
    let proximaColuna = 0

    const posicionarRamo = (cargoId: string): number => {
      const filhos = filhosPorCargoId.get(cargoId) ?? []

      if (!filhos.length) {
        colunaPorCargoId.set(cargoId, proximaColuna)
        return proximaColuna++
      }

      const colunasDosFilhos = filhos.map(posicionarRamo)
      const coluna = (colunasDosFilhos[0] + colunasDosFilhos[colunasDosFilhos.length - 1]) / 2
      colunaPorCargoId.set(cargoId, coluna)
      return coluna
    }

    raizes.forEach(posicionarRamo)

    colunaPorCargoId.forEach((coluna, cargoId) => {
      const no = noPorCargoId.get(cargoId)
      if (!no) return

      nosPosicionados.set(cargoId, {
        ...no,
        position: {
          x: deslocamentoXDoBloco + coluna * (LARGURA_NO + ESPACO_HORIZONTAL),
          y: yPorCargoId.get(cargoId) ?? 0
        }
      })
    })

    deslocamentoXDoBloco += proximaColuna * (LARGURA_NO + ESPACO_HORIZONTAL) + ESPACO_ENTRE_BLOCOS
  }

  return nos.map((no) => nosPosicionados.get(no.id) ?? no)
}

function calcularMediaDePontuacao(cargo?: CargoOrganogramaResponse): number {
  const niveis = cargo?.niveisPontuados ?? []
  if (!niveis.length) return 0
  return niveis.reduce((soma, nivel) => soma + nivel.pontuacaoTotal, 0) / niveis.length
}

export function mapearCargosPorId(cargos: CargoOrganogramaResponse[]): Map<string, CargoOrganogramaResponse> {
  const cargoPorId = new Map<string, CargoOrganogramaResponse>()

  function percorrer(lista: CargoOrganogramaResponse[]): void {
    for (const cargo of lista) {
      if (cargoPorId.has(cargo.id)) continue
      cargoPorId.set(cargo.id, cargo)
      percorrer(cargo.subordinados ?? [])
    }
  }

  percorrer(cargos)
  return cargoPorId
}

function encontrarCargoPorId(lista: CargoOrganogramaResponse[], cargoId: string): CargoOrganogramaResponse | undefined {
  for (const cargo of lista) {
    if (cargo.id === cargoId) return cargo
    const encontrado = encontrarCargoPorId(cargo.subordinados, cargoId)
    if (encontrado) return encontrado
  }
  return undefined
}
